/* eslint-disable solid/no-innerhtml */

import {
  createEffect,
  createSignal,
  For,
  Match,
  on,
  onMount,
  onCleanup,
  Show,
  Switch,
} from 'solid-js'
import scrollIntoView from 'scroll-into-view-if-needed'
import { lock, unlock } from 'tua-body-scroll-lock'
import { ACTIVE_CLASS, MENU_OPEN_CLASS } from '../../utilities/constants'

export default function SmallNavigation(props) {
  const [selectedSubMenu, setSelectedSubmenu] = createSignal(null)
  const [subMenuGroups, setSubMenuGroups] = createSignal(null)

  let navigationContainer

  const handleToggleMenu = () => {
    setSelectedSubmenu(null)

    const isActive = document.documentElement.classList.toggle(MENU_OPEN_CLASS)

    if (isActive) {
      lock(navigationContainer)
    } else {
      unlock(navigationContainer)
    }
  }

  const handleLinkClick = (e) => {
    const href = e.currentTarget.getAttribute('href')

    if (href.startsWith('#')) {
      const targetEl = document.body.querySelector(href)

      if (targetEl) {
        e.preventDefault()

        handleToggleMenu()

        targetEl.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })

        setTimeout(() => {
          scrollIntoView(targetEl, {
            scrollMode: 'if-needed',
            behavior: 'smooth',
            block: 'start',
          })
        }, 400)
      }
    }
  }

  const handleToggleSubMenu = (itemId) => {
    setSelectedSubmenu(itemId)
  }

  onMount(() => {
    navigationContainer = document.body.querySelector('.js-navigationContainer')
  })

  onCleanup(() => {
    unlock(navigationContainer)
    document.documentElement.classList.remove(MENU_OPEN_CLASS)
  })

  createEffect(
    on(
      () => selectedSubMenu(),
      () => {
        if (selectedSubMenu()) {
          let parentItem = props.data.main.find(
            (item) => item.id === selectedSubMenu(),
          )

          if (!parentItem) {
            for (const group of subMenuGroups()) {
              parentItem = group.children.find(
                (item) => item.id === selectedSubMenu(),
              )

              if (parentItem) {
                break
              }
            }
          }

          if (!parentItem) {
            console.error('Could not find parent item')
            return
          }

          const groups = parentItem.children.reduce((acc, item) => {
            if (!acc[parentItem.title]) {
              acc[parentItem.title] = { ...parentItem }
              acc[parentItem.title].children = []
            }

            if (item?.children?.length) {
              acc[item.title] = { ...item }
            } else {
              acc[parentItem.title].children.push(item)
            }

            return acc
          }, {})

          setSubMenuGroups(Object.values(groups))
        } else {
          setSubMenuGroups(null)
        }
      },
      { defer: true },
    ),
  )

  return (
    <div class="Navigation">
      <div class="Navigaiton-inner">
        <div class="Navigation-head">
          <div class="Navigation-headLeft">
            <Switch
              fallback={
                <div class="Navigation-headLogo">
                  <a class="Navigation-headLogoLink" href="/">
                    <svg
                      class="Navigation-headLogoSymbol"
                      width="75"
                      height="24"
                      aria-hidden="true"
                    >
                      <use xlink:href="#symbol-logo" />
                    </svg>
                    <span class="u-hiddenVisually">
                      Komm - Sveriges Kommunikationsbyråer
                    </span>
                  </a>
                </div>
              }
            >
              <Match when={selectedSubMenu()}>
                <button
                  class="Navigation-headLeftButton Button Button--fill Button--sm"
                  type="button"
                  onClick={() => setSelectedSubmenu(null)}
                >
                  <span class="Button-text">Tillbaka</span>
                </button>
              </Match>
            </Switch>
          </div>
          <div class="Navigation-headRight">
            <button
              class="Navigation-headRightButton Button"
              onClick={handleToggleMenu}
              type="button"
            >
              <span class="u-hiddenVisually">Meny</span>
              <svg
                class="Navigation-headRightButtonSymbol Button-symbol"
                width="10"
                height="10"
                aria-hidden="true"
              >
                <use xlink:href="#symbol-cross" />
              </svg>
            </button>
          </div>
        </div>
        <div class="Navigation-container js-navigationContainer">
          <div class="Navigation-content">
            <Switch
              fallback={
                <>
                  <ul class="Navigation-main">
                    <For each={props?.data.main}>
                      {(item) => {
                        return (
                          <li
                            classList={{
                              'Navigation-item': true,
                              'Navigation-item--top': true,
                              [ACTIVE_CLASS]: window.location.href === item.url,
                            }}
                          >
                            <Switch
                              fallback={
                                <div class="Navigation-itemInner">
                                  <a
                                    class="Navigation-itemLink"
                                    href={item.url}
                                    innerHTML={item.title}
                                    onClick={handleLinkClick}
                                    target={item.target}
                                  />
                                </div>
                              }
                            >
                              <Match when={item?.children?.length}>
                                <button
                                  class="Navigation-itemInner Button"
                                  type="button"
                                  onClick={() => handleToggleSubMenu(item.id)}
                                >
                                  <span
                                    class="Navigation-itemLink"
                                    innerHTML={item.title}
                                  />
                                  <svg
                                    class="Navigation-itemButtonSymbol Button-symbol"
                                    width="16"
                                    height="10"
                                    aria-hidden="true"
                                  >
                                    <use xlink:href="#symbol-chevron-down" />
                                  </svg>
                                </button>
                              </Match>
                            </Switch>
                          </li>
                        )
                      }}
                    </For>
                  </ul>
                  <Show when={props?.data?.actions?.length}>
                    <ul class="Navigation-actions">
                      <For each={props?.data?.actions}>
                        {(item, index) => (
                          <li>
                            <a
                              classList={{
                                Button: true,
                                'Button--md': true,
                                'Button--outline': index() % 2,
                                'Button--fill': !(index() % 2),
                              }}
                              href={item.url}
                              target={item.target}
                              onClick={handleLinkClick}
                            >
                              <span class="Button-text">{item.title}</span>
                            </a>
                          </li>
                        )}
                      </For>
                    </ul>
                  </Show>
                  <Show when={props?.data?.secondary?.length}>
                    <ul class="Navigation-secondary">
                      <For each={props.data.secondary}>
                        {(item) => (
                          <li class="Navigation-secondaryItem">
                            <a
                              class="Navigation-secondaryLink"
                              href={item.url}
                              target={item.target}
                              onClick={handleLinkClick}
                            >
                              <span
                                class="Navigation-secondaryLinkText"
                                innerHTML={item.title}
                              />
                            </a>
                          </li>
                        )}
                      </For>
                    </ul>
                  </Show>
                </>
              }
            >
              <Match when={subMenuGroups()}>
                <div class="Navigation-main">
                  <For each={subMenuGroups()}>
                    {(group) => {
                      return (
                        <ul class="Navigation-itemGroup">
                          <li
                            classList={{
                              'Navigation-item': true,
                              'Navigation-item--top': true,
                              [ACTIVE_CLASS]:
                                window.location.href === group.url,
                            }}
                          >
                            <div class="Navigation-itemInner">
                              <a
                                class="Navigation-itemLink"
                                href={group.url}
                                innerHTML={group.title}
                                target={group?.target}
                                onClick={handleLinkClick}
                              />
                              {group.buttonText && (
                                <a
                                  class="Navigation-itemButton Navigation-itemButton--text Button Button--outline Button--sm"
                                  href={group.url}
                                  target={group.target}
                                >
                                  <span class="Button-text">
                                    {group.buttonText}
                                  </span>
                                </a>
                              )}
                            </div>
                          </li>
                          <For each={group.children}>
                            {(item) => (
                              <li
                                classList={{
                                  'Navigation-item': true,
                                  [ACTIVE_CLASS]:
                                    window.location.href === item.url,
                                }}
                              >
                                <Switch
                                  fallback={
                                    <div class="Navigation-itemInner">
                                      <a
                                        class="Navigation-itemLink"
                                        href={item.url}
                                        target={group.target}
                                        innerHTML={item.title}
                                        onClick={handleLinkClick}
                                      />
                                    </div>
                                  }
                                >
                                  <Match when={item?.children?.length}>
                                    <button
                                      class="Navigation-itemInner Button"
                                      type="button"
                                      onClick={() =>
                                        handleToggleSubMenu(item.id)
                                      }
                                    >
                                      <span
                                        class="Navigation-itemLink"
                                        innerHTML={item.title}
                                      />
                                      <svg
                                        class="Navigation-itemButtonSymbol Button-symbol"
                                        width="16"
                                        height="10"
                                        aria-hidden="true"
                                      >
                                        <use xlink:href="#symbol-chevron-down" />
                                      </svg>
                                    </button>
                                  </Match>
                                </Switch>
                              </li>
                            )}
                          </For>
                        </ul>
                      )
                    }}
                  </For>
                </div>
              </Match>
            </Switch>
          </div>
        </div>
      </div>
    </div>
  )
}
